import {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useRef,
} from 'react'

/** Run the registered save (same as Cmd+S) before leaving; no-op if none registered. */
export type RunSaveBeforeLeave = () => Promise<void>

type ContextValue = {
	registerSave: (save: RunSaveBeforeLeave) => () => void
	runSaveBeforeLeave: RunSaveBeforeLeave
}

const noop: RunSaveBeforeLeave = () => Promise.resolve()

const SaveBeforeLeaveContext = createContext<ContextValue | undefined>(undefined)

export const SaveBeforeLeaveProvider = ({ children }: { children: ReactNode }) => {
	const savesRef = useRef(new Map<symbol, RunSaveBeforeLeave>())

	const registerSave = useCallback((save: RunSaveBeforeLeave) => {
		const id = Symbol('saveBeforeLeave')
		savesRef.current.set(id, save)
		return () => {
			savesRef.current.delete(id)
		}
	}, [])

	const runSaveBeforeLeave = useCallback<RunSaveBeforeLeave>(async () => {
		for (const save of [...savesRef.current.values()]) {
			await save()
		}
	}, [])

	return (
		<SaveBeforeLeaveContext.Provider
			value={{ registerSave, runSaveBeforeLeave }}
		>
			{children}
		</SaveBeforeLeaveContext.Provider>
	)
}

export const useRunSaveBeforeLeave = (): RunSaveBeforeLeave => {
	const ctx = useContext(SaveBeforeLeaveContext)
	return ctx?.runSaveBeforeLeave ?? noop
}

export const useRegisterSaveBeforeLeave = () => {
	const ctx = useContext(SaveBeforeLeaveContext)
	return ctx?.registerSave ?? (() => () => {})
}
